import { applyRounding, calculateDB, calculateGrowth } from "./calculations"

/**
 * Rearranges and enriches revenue data by calculating growth and summary statistics.
 * @param {Object} data - The revenue data.
 * @param {Array} data.currentYearRevenue - The revenue data for the current year.
 * @param {Array} data.lastYearRevenue - The revenue data for the last year.
 * @returns {Array} - The rearranged and enriched revenue data.
 */
export function rearrangeAndEnrichRevenueData({ currentYearRevenue, lastYearRevenue }) {
  const transformedArray = transformData({
    currentYearRevenue,
    lastYearRevenue,
  })

  const enrichedArray = addMarginInPercent(transformedArray)
  return addGrowthData(enrichedArray)
}

/**
 * Calculates summary statistics for the revenue data.
 * @param {Array} statsByArticleMainGroup - The revenue data grouped by article main group.
 * @returns {Object} - The summary statistics.
 */
export function calculateSummary(statsByArticleMainGroup) {
  const currentYearRevenue = statsByArticleMainGroup.reduce((acc, item) => {
    const revenue = Number(item.currentYearRevenue?.revenue)
    return applyRounding(acc + (isNaN(revenue) ? 0 : revenue))
  }, 0)

  const lastYearRevenue = statsByArticleMainGroup.reduce((acc, item) => {
    const revenue = Number(item.lastYearRevenue?.revenue)
    return applyRounding(acc + (isNaN(revenue) ? 0 : revenue))
  }, 0)

  const revenueGrowth = calculateGrowth(currentYearRevenue, lastYearRevenue)

  const lastYearMargin = statsByArticleMainGroup.reduce((acc, item) => {
    const margin = Number(item.lastYearRevenue?.margin)
    return acc + (isNaN(margin) ? 0 : margin)
  }, 0)

  const lastYearMarginInPercent = calculateDB(lastYearMargin, lastYearRevenue)

  const currentYearMargin = statsByArticleMainGroup.reduce((acc, item) => {
    const margin = Number(item.currentYearRevenue?.margin)
    return acc + (isNaN(margin) ? 0 : margin)
  }, 0)

  const currentYearMarginInPercent = calculateDB(currentYearMargin, currentYearRevenue)

  const marginGrowthInPercent = calculateGrowth(currentYearMarginInPercent, lastYearMarginInPercent)

  const marginGrowth = calculateGrowth(currentYearMargin, lastYearMargin)

  return {
    currentYearRevenue,
    lastYearRevenue,
    revenueGrowth,
    marginGrowth,
    marginGrowthInPercent,
    lastYearMargin,
    currentYearMargin,
    lastYearMarginInPercent,
    currentYearMarginInPercent,
  }
}

function addGrowthData(transformedArray) {
  return transformedArray.map((item) => {
    const { currentYearRevenue, lastYearRevenue } = item
    const revenueGrowth = calculateGrowth(Number(currentYearRevenue?.revenue), Number(lastYearRevenue?.revenue))

    const marginGrowth = calculateGrowth(Number(currentYearRevenue?.margin), Number(lastYearRevenue?.margin))

    const marginGrowthInPercent = calculateGrowth(
      Number(currentYearRevenue?.marginInPercent),
      Number(lastYearRevenue?.marginInPercent),
    )
    return {
      ...item,
      revenueGrowth,
      marginGrowth,
      marginGrowthInPercent,
    }
  })
}

/**
 * Transforms the revenue data by grouping it by article main group.
 * @param {Object} data - The revenue data.
 * @param {Array} data.currentYearRevenue - The revenue data for the current year.
 * @param {Array} data.lastYearRevenue - The revenue data for the last year.
 * @returns {Array} - The transformed revenue data.
 */
function transformData({ currentYearRevenue, lastYearRevenue }) {
  const transformedData = {}

  currentYearRevenue?.forEach(({ articleMainGroup, ...rest }) => {
    transformedData[articleMainGroup] = {
      articleMainGroup,
      currentYearRevenue: rest,
    }
  })

  lastYearRevenue?.forEach(({ articleMainGroup, ...rest }) => {
    if (transformedData[articleMainGroup]) {
      transformedData[articleMainGroup].lastYearRevenue = rest
    } else {
      transformedData[articleMainGroup] = {
        articleMainGroup,
        lastYearRevenue: rest,
      }
    }
  })

  return sortByArticleMainGroup(transformedData)
}

/**
 * Sorts the transformed revenue data by article main group.
 * Because the articleMainGroup keys may have leading zeros.
 * The sorting is done by filling the main item group with leading zeros and using localeCompare for comparison.
 * To ensure that the article main groups are sorted in ascending order.
 * @param {Object} transformedData - The transformed revenue data.
 * @returns {Array} - The sorted revenue data.
 */
function sortByArticleMainGroup(transformedData) {
  return Object.values(transformedData).sort((a, b) => {
    const groupA = a.articleMainGroup.padStart(2, "0")
    const groupB = b.articleMainGroup.padStart(2, "0")
    return groupA.localeCompare(groupB)
  })
}

function addMarginInPercent(transformedArray) {
  return transformedArray.map((revenue) => {
    let currentYearRevenue = revenue?.currentYearRevenue
    let lastYearRevenue = revenue?.lastYearRevenue

    if (currentYearRevenue) {
      currentYearRevenue = {
        ...currentYearRevenue,
        marginInPercent: calculateDB(currentYearRevenue?.margin, currentYearRevenue?.revenue),
      }
    }

    if (lastYearRevenue) {
      lastYearRevenue = {
        ...lastYearRevenue,
        marginInPercent: calculateDB(lastYearRevenue?.margin, lastYearRevenue?.revenue),
      }
    }
    return {
      ...revenue,
      currentYearRevenue: currentYearRevenue,
      lastYearRevenue: lastYearRevenue,
    }
  })
}

/**
 * Rearranges and enriches YTD revenue data by calculating growth and summary statistics.
 *
 * @param {Object} props
 * @param {Array} props.currentYearRevenue - The revenue data for the current year.
 * @param {Array} props.lastYearRevenue - The revenue data for the last year.
 * @returns {Array}
 */
export function rearrangeYtdRevenueData({ currentYearRevenue, lastYearResponse }) {
  const lastYearData = Object.values(lastYearResponse).reduce((acc, month) => {
    month.forEach(({ articleMainGroup, revenue, margin }) => {
      if (!acc[articleMainGroup]) {
        acc[articleMainGroup] = { articleMainGroup, revenue: 0, margin: 0 }
      }
      acc[articleMainGroup].revenue += revenue
      acc[articleMainGroup].margin += margin
    })
    return acc
  }, {})

  const lastYearRevenue = Object.values(lastYearData).map(({ articleMainGroup, revenue, margin }) => ({
    articleMainGroup,
    revenue: applyRounding(revenue),
    margin: applyRounding(margin),
  }))

  const transformedArray = transformData({
    currentYearRevenue,
    lastYearRevenue,
  })

  const enrichedArray = addMarginInPercent(transformedArray)

  return addGrowthData(enrichedArray)
}
